import axios from 'axios';
import { contractorAuth } from './contractorAuth';
import { isStandalone, platformHint } from './pwa';

/**
 * Push notifications on this device (migration 129): the browser half of
 * "Notify this device" on the Notifications page.
 *
 * WHAT THIS DEVICE CAN DO - `pushSupport()` answers one of:
 *
 *   'ok'            a service worker and the Push API are here: offer it
 *   'ios-install'   an iPhone or iPad in a browser tab. Apple only allows
 *                   web push from the app added to the Home Screen (iOS
 *                   16.4 or later), so the page explains that instead of
 *                   offering a button that cannot work
 *   'ios-old'       the home-screen app, but an iOS too old for push
 *   'unsupported'   no Push API at all (an old browser, a private window
 *                   in some browsers, or the dev server, which registers
 *                   no service worker)
 *
 * TURNING IT ON (`turnOn`) asks the browser for permission - the one moment
 * that makes sense, because the person just pressed the button - then
 * subscribes with the server's VAPID public key (userVisibleOnly: every
 * push shows a notification) and sends the subscription and a label the
 * person will recognise to the server. TURNING IT OFF on this device
 * unsubscribes the browser too.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_BASE}/notification-preferences/push`;

const hasWindow = () => typeof window !== 'undefined' && typeof navigator !== 'undefined';

export function pushSupport() {
  if (!hasWindow()) return 'unsupported';
  const hint = platformHint();
  const api = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hint.os === 'ios') {
    if (!isStandalone()) return 'ios-install';
    return api ? 'ok' : 'ios-old';
  }
  return api ? 'ok' : 'unsupported';
}

/** The browser's permission for notifications: 'granted' | 'denied' | 'default' | 'unsupported'. */
export function permission() {
  return hasWindow() && 'Notification' in window ? Notification.permission : 'unsupported';
}

/** "Chrome on Windows", "iPhone (home-screen app)" - what the device list calls this one. */
export function deviceLabel() {
  const { os, browser } = platformHint();
  const OS = { windows: 'Windows', mac: 'Mac', linux: 'Linux', android: 'Android', ios: 'iPhone' };
  const BROWSER = { chrome: 'Chrome', edge: 'Edge', firefox: 'Firefox', safari: 'Safari' };
  if (os === 'ios') {
    const ipad = typeof navigator !== 'undefined' && /iPad|Macintosh/.test(navigator.userAgent || '');
    return `${ipad ? 'iPad' : 'iPhone'} (home-screen app)`;
  }
  const where = OS[os] || 'this computer';
  const app = isStandalone() ? 'Jarvis app' : (BROWSER[browser] || 'Browser');
  return `${app} on ${where}`;
}

/** The base64url VAPID key as the bytes PushManager wants. */
function keyBytes(base64url) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = window.atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** The service worker, waited for at most `ms` (none on the dev server). */
async function registration(ms = 8000) {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), ms))
  ]);
}

/** This browser's current subscription's endpoint, or null. */
export async function currentEndpoint() {
  try {
    if (pushSupport() !== 'ok') return null;
    const reg = await registration(3000);
    const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
    return sub ? sub.endpoint : null;
  } catch {
    return null;
  }
}

class PushRefused extends Error {}

/**
 * Turn on this device. Answers the page's fresh data. Throws a sentence the
 * page can show when the browser says no.
 */
export async function turnOn(publicKey) {
  if (!publicKey) throw new PushRefused('Push notifications are not set up on this server.');
  const asked = await Notification.requestPermission();
  if (asked !== 'granted') {
    throw new PushRefused(asked === 'denied'
      ? 'Notifications are blocked for Jarvis in this browser. Allow them in the browser’s site settings, then try again.'
      : 'Notifications were not allowed, so nothing was turned on.');
  }
  const reg = await registration();
  if (!reg || !reg.pushManager) throw new PushRefused('This browser cannot receive notifications from Jarvis here.');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  }
  const json = typeof sub.toJSON === 'function' ? sub.toJSON() : sub;
  const r = await axios.post(`${BASE}/devices`, {
    subscription: { endpoint: json.endpoint, keys: json.keys }, label: deviceLabel()
  }, contractorAuth());
  return r.data;
}

/** Turn off one device; when it is this browser, unsubscribe here too. */
export async function turnOff(device, { here = false } = {}) {
  const r = await axios.delete(`${BASE}/devices/${device.id}`, contractorAuth());
  if (here) {
    try {
      const reg = await registration(3000);
      const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
      if (sub) await sub.unsubscribe();
    } catch { /* the server has forgotten it either way */ }
  }
  return r.data;
}

export async function sendTest(device) {
  const r = await axios.post(`${BASE}/devices/${device.id}/test`, {}, contractorAuth());
  return r.data;
}

export async function saveQuiet(body) {
  const r = await axios.put(`${BASE}/quiet`, body, contractorAuth());
  return r.data;
}

export { PushRefused };
